import React from "react";
import { useState, useEffect } from "react";
import { RadioGroup } from "@headlessui/react";
import { useTranslation } from "react-i18next";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Irk boyutu seçenekleri (sadece köpekler için) — labels resolved inside component via t()
const breedSizeOptionDefs = [
  { id: "mini", labelKey: "breedSizeMini", maxWeight: 10 },
  { id: "medium", labelKey: "breedSizeMedium", maxWeight: 25 },
  { id: "large", labelKey: "breedSizeLarge", maxWeight: 100 },
];

const neutralOptions = [{ neutral: true }, { neutral: false }];

function PetAge({ petInfo, setPetInfo, setIsComplete, petType }) {
  const { t } = useTranslation();
  const [petName, setPetName] = useState(petInfo.name || '');

  // Resolve breed size labels via i18n
  const breedSizeOptions = breedSizeOptionDefs.map(opt => ({ ...opt, label: t(opt.labelKey) }));

  // Life stage options - simplified
  const lifeStageOptions = petType === "cat" ? [
    { id: "kitten", label: t("kitten") || "Yavru", description: "0-12 ay", ageMonths: 6 },
    { id: "adult", label: t("adult") || "Yetişkin", description: "1-7 yaş", ageMonths: 36 },
    { id: "senior", label: t("senior") || "Yaşlı", description: "7+ yaş", ageMonths: 96 }
  ] : [
    { id: "puppy", label: t("puppy") || "Yavru", description: "0-12 ay", ageMonths: 6 },
    { id: "adult", label: t("adult") || "Yetişkin", description: "1-7 yaş", ageMonths: 36 },
    { id: "senior", label: t("senior") || "Yaşlı", description: "7+ yaş", ageMonths: 96 }
  ];

  const getInitialLifeStage = () => {
    if (petInfo.lifeStage) {
      return lifeStageOptions.find(opt => opt.id === petInfo.lifeStage) || null;
    }
    return null;
  };

  const getInitialNeutral = () => {
    if (petInfo.isNeutral !== undefined && petInfo.isNeutral !== null) {
      return neutralOptions.find(n => n.neutral === petInfo.isNeutral) || null;
    }
    return null;
  };

  const getInitialBreedSize = () => {
    if (petInfo.breedSize) {
      return breedSizeOptions.find(s => s.id === petInfo.breedSize) || null;
    }
    return null;
  };

  const [selectedLifeStage, setSelectedLifeStage] = useState(getInitialLifeStage);
  const [isNeutral, setIsNeutral] = useState(getInitialNeutral);
  const [breedSize, setBreedSize] = useState(getInitialBreedSize);

  // Check if form is complete
  useEffect(() => {
    const lifeStageComplete = selectedLifeStage !== null;
    const neutralComplete = isNeutral !== null;
    const breedSizeComplete = petType === "cat" || breedSize !== null;

    const isFormComplete = lifeStageComplete && neutralComplete && breedSizeComplete;

    // Always update petInfo when any selection changes
    setPetInfo(prev => ({
      ...prev,
      lifeStage: selectedLifeStage?.id || prev.lifeStage,
      ageMonths: selectedLifeStage?.ageMonths || prev.ageMonths,
      isNeutral: isNeutral !== null ? isNeutral.neutral : prev.isNeutral,
      breedSize: breedSize ? breedSize.id : prev.breedSize,
      breed: breedSize ? t(breedSize.labelKey) : prev.breed,
    }));

    // Only mark as complete if all required fields are filled
    setIsComplete(isFormComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLifeStage, isNeutral, breedSize, petType]);

  // Sync pet name to petInfo
  useEffect(() => {
    setPetInfo(prev => ({ ...prev, name: petName }));
  }, [petName]);

  return (
    <div className="w-full max-w-lg flex flex-col px-4 sm:px-0">
      {/* Pet Name - Optional */}
      <div className="mb-6">
        <h2 className="font-poppins text-xl text-yellow-700 font-bold mb-1">
          {t("tellUsAboutYourPet") || "Dostunuzu tanıyalım"}
        </h2>
        <p className="font-poppins text-yellow-600 text-sm mb-4">
          {t("createNutritionPlan") || "Özel beslenme planı oluşturmak için bilgi verin"}
        </p>
        <div className="relative z-0 w-full group">
          <input
            onChange={(e) => setPetName(e.target.value)}
            type="text"
            name="petName"
            className="block py-2.5 px-0 w-full text-lg text-yellow-600 bg-transparent border-0 border-b-2 border-yellow-400 appearance-none focus:outline-none focus:ring-0 focus:border-kito-green peer
            [&:-webkit-autofill]:!bg-transparent [&:-webkit-autofill]:!bg-none [&:-webkit-autofill]:[-webkit-box-shadow:0_0_0_30px_white_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:#ca8a04]"
            placeholder=" "
            value={petName}
          />
          <label
            htmlFor="petName"
            className="peer-focus:font-medium font-poppins absolute left-0 text-sm text-yellow-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-kito-green peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
          >
            {petType === 'dog' ? (t("petName") || "Köpeğinizin adı") : (t("catPetName") || "Kedinizin adı")} <span className="text-gray-400 text-xs">({t("optional") || "opsiyonel"})</span>
          </label>
        </div>
      </div>

      {/* Life Stage Selection */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="font-medium font-poppins text-lg text-yellow-600 text-left">
            {petType === "cat" ? t("catPetAge") || "Kedinizin yaşam evresi" : t("dogPetAge") || "Köpeğinizin yaşam evresi"}
          </h2>
        </div>

        <RadioGroup value={selectedLifeStage} onChange={setSelectedLifeStage} className="mt-2">
          <RadioGroup.Label className="sr-only">Yaşam evresi seçin</RadioGroup.Label>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
            {lifeStageOptions.map((option) => (
              <RadioGroup.Option
                key={option.id}
                value={option}
                className={({ checked }) =>
                  classNames(
                    "cursor-pointer focus:outline-none",
                    checked || petInfo.lifeStage === option.id
                      ? "bg-kito-green border-transparent text-white hover:bg-opacity-90"
                      : "bg-white border-yellow-200 text-yellow-800 hover:bg-yellow-50",
                    "border rounded-md py-2 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                  )
                }
              >
                <RadioGroup.Label as="span">{option.label}</RadioGroup.Label>
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>

        {/* Info box */}
        <div className="mt-3 bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded">
          <div className="flex items-start">
            <div className="ml-1 flex-1">
              <p className="text-sm text-yellow-800 font-poppins break-words text-left">
                <span className="font-bold">{t("ageRangesPuppy")}</span> {t("ageRangesPuppyValue")} • <span className="font-bold">{t("ageRangesAdult")}</span> {t("ageRangesAdultValue")} • <span className="font-bold">{t("ageRangesSenior")}</span> {t("ageRangesSeniorValue")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Separator */}
      <div className="border-b-2 border-yellow-400 mb-6"></div>

      {/* Breed Size - Dogs Only */}
      {petType === "dog" && (
        <>
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <h2 className="font-medium font-poppins text-lg text-yellow-600 text-left">
                {t("breedSize") || "Köpeğinizin boyutu"}
              </h2>
            </div>

            <RadioGroup value={breedSize} onChange={setBreedSize} className="mt-2">
              <RadioGroup.Label className="sr-only">Irk boyutu seçin</RadioGroup.Label>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
                {breedSizeOptions.map((option) => (
                  <RadioGroup.Option
                    key={option.id}
                    value={option}
                    className={({ checked }) =>
                      classNames(
                        "cursor-pointer focus:outline-none",
                        checked || petInfo.breedSize === option.id
                          ? "bg-kito-green border-transparent text-white hover:bg-opacity-90"
                          : "bg-white border-yellow-200 text-yellow-800 hover:bg-yellow-50",
                        "border rounded-md py-2 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                      )
                    }
                  >
                    <RadioGroup.Label as="span">{option.label}</RadioGroup.Label>
                  </RadioGroup.Option>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Separator */}
          <div className="border-b-2 border-yellow-400 mb-6"></div>
        </>
      )}

      {/* Neutered Question */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="font-medium font-poppins text-lg text-yellow-600 text-left">
            {petType === "cat"
              ? (t("isCatNeutered") || "Kediniz kısırlaştırıldı mı?")
              : (t("isDogNeutered") || "Köpeğiniz kısırlaştırıldı mı?")}
          </h2>
        </div>

        <RadioGroup value={isNeutral} onChange={setIsNeutral} className="mt-2">
          <RadioGroup.Label className="sr-only">Kısırlaştırma durumu</RadioGroup.Label>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
            {neutralOptions.map((option) => (
              <RadioGroup.Option
                key={option.neutral ? "yes" : "no"}
                value={option}
                className={({ checked }) =>
                  classNames(
                    "cursor-pointer focus:outline-none",
                    checked || petInfo.isNeutral === option.neutral
                      ? "bg-kito-green border-transparent text-white hover:bg-opacity-90"
                      : "bg-white border-yellow-200 text-yellow-800 hover:bg-yellow-50",
                    "border rounded-md py-2 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                  )
                }
              >
                <RadioGroup.Label as="span">
                  {option.neutral ? (t("yes") || "EVET") : (t("no") || "HAYIR")}
                </RadioGroup.Label>
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

export default PetAge;
